import { useState } from "react";
import {
  Button,
  Card,
  Group,
  Text,
  SimpleGrid,
  Badge,
  Stack,
  ActionIcon,
  Menu,
  Skeleton,
  Box,
} from "@mantine/core";
import { ZoomableImage } from "../../components/common/ZoomableImage";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { rideTypesApi } from "../../api/rideTypes";
import type { RideType } from "../../types/domain";
import { PageHeader } from "../../components/common/PageHeader";
import { QueryError } from "../../components/common/QueryError";
import { formatMoney, en } from "../../lib/format";
import { ExportMenu } from "../../components/common/ExportMenu";
import { type ExportField } from "../../lib/export";
import { errorMessage } from "../../lib/axios";
import {
  RideTypeFormModal,
  type RideTypeFormValues,
} from "./RideTypeFormModal";

const rideTypeExportFields: ExportField<RideType>[] = [
  { label: "Name", value: (rt) => en(rt.name) },
  { label: "Vehicle type", value: (rt) => rt.vehicleType },
  { label: "Active", value: (rt) => (rt.isActive ? "Yes" : "No") },
  { label: "Starting Fare", value: (rt) => rt.minimumFare },
  { label: "Per km", value: (rt) => rt.farePerKm },
  { label: "Per min", value: (rt) => rt.farePerMin },
  { label: "Waiting/min", value: (rt) => rt.waitingFeePerMin },
  { label: "Capacity", value: (rt) => rt.numberOfPassengers },
];

export function RideTypesPage() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RideType | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["ride-types"],
    queryFn: () => rideTypesApi.list(),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["ride-types"] });
  }

  const saveMutation = useMutation({
    mutationFn: (values: RideTypeFormValues) =>
      editing
        ? rideTypesApi.update(editing._id, values)
        : rideTypesApi.create(values),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: editing ? "Ride type updated" : "Ride type created",
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rideTypesApi.remove(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Ride type deleted" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  function confirmDelete(rt: RideType) {
    modals.openConfirmModal({
      title: "Delete ride type",
      children: (
        <Text size="sm">
          Delete <b>{en(rt.name)}</b>? This soft-deletes the category.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(rt._id),
    });
  }

  return (
    <>
      <PageHeader
        title="Ride Types & Pricing"
        subtitle="Fare configuration per vehicle category"
        actions={
          <Group gap="sm">
            <ExportMenu
              filename="ride-types"
              fields={rideTypeExportFields}
              disabled={isLoading}
              fetchAll={async () => data?.items ?? []}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New ride type
            </Button>
          </Group>
        }
      />

      {error && (
        <Box mb="lg">
          <QueryError error={error} onRetry={refetch} />
        </Box>
      )}

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} h={220} radius="md" />
          ))}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {(data?.items ?? []).map((rt) => (
            <Card key={rt._id} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" mb="sm">
                <Group>
                  <Box w={48}>
                    <ZoomableImage
                      src={rt.imageUrl}
                      w={48}
                      h={48}
                      fit="contain"
                      alt={en(rt.name)}
                      title={en(rt.name)}
                    />
                  </Box>
                  <Stack gap={2}>
                    <Text fw={600}>{en(rt.name)}</Text>
                    <Badge
                      size="sm"
                      variant="light"
                      color={rt.isActive ? "green" : "gray"}
                    >
                      {rt.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Stack>
                </Group>
                <Menu position="bottom-end" withArrow>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={16} />}
                      onClick={() => {
                        setEditing(rt);
                        setFormOpen(true);
                      }}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => confirmDelete(rt)}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>

              <Stack gap={4}>
                <PriceRow label="Starting Fare" value={rt.minimumFare} />
                <PriceRow label="Per km" value={rt.farePerKm} />
                <PriceRow label="Per min" value={rt.farePerMin} />
                <PriceRow label="Waiting / min" value={rt.waitingFeePerMin} />
                <PriceRow label="Cancellation fee" value={rt.cancellationFee ?? 0} />
                <Group justify="space-between" mt={4}>
                  <Text size="sm" c="dimmed">
                    Capacity
                  </Text>
                  <Text size="sm">{rt.numberOfPassengers ?? "—"} pax</Text>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <RideTypeFormModal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        editing={editing}
        rideTypes={data?.items ?? []}
        loading={saveMutation.isPending}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </>
  );
}

function PriceRow({ label, value }: { label: string; value?: number }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {formatMoney(value)}
      </Text>
    </Group>
  );
}
